import {
  Injectable,
  ApplicationRef,
  ComponentFactoryResolver,
  Injector,
  ComponentRef
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AppSettings } from './app-settings';
import { AuthService } from '@auth0/auth0-angular';
import { TimeoutWarningComponent } from '../modules/myPrimeRx/components/timeout-warning/timeout-warning.component';
import { PeAuthService } from '../core/services/auth.service';

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  idleTimer: ReturnType<typeof setTimeout> | null = null;
  warningTimer: ReturnType<typeof setTimeout> | null = null;
  modalRef: ComponentRef<TimeoutWarningComponent> | null = null;
  isWarningVisible = false;

  constructor(
    private _authService: AuthService,
    private _router: Router,
    private appRef: ApplicationRef,
    public peAuthService: PeAuthService,
    private resolver: ComponentFactoryResolver,
    private injector: Injector
  ) {
    this.initListeners();
    this.startTimers();

    // Start timers when navigating away from public pages (login/callback)
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(() => {
      if (!this.isOnPublicPage()) {
        this.resetTimers();
      } else {
        this.clearTimers();
        if (this.isWarningVisible) {
          this.destroyModal();
          this.isWarningVisible = false;
        }
      }
    });
  }

  private initListeners(): void {
    const events: string[] = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, () => { this.handleUserActivity(); });
    });
  }

  private handleUserActivity(): void {
    if (!this.isWarningVisible && !this.isOnPublicPage()) {
      this.resetTimers();
    }
  }

  private isOnPublicPage(): boolean {
    const url = this._router?.url || '';
    return url.includes('/login') || url.includes('/callback') || url.includes('/error') || url === '/';
  }

  private startTimers(): void {
    if (this.isOnPublicPage()) {
      return;
    }

    this.clearTimers();

    const timeoutMs = Number(AppSettings.sessionTimeoutInMs);
    const warningBeforeMs = Number(AppSettings.warningBeforeInMs);
    const warningTime = timeoutMs - warningBeforeMs;    
    this.warningTimer = setTimeout(() => {
      this.showWarningDialog();
    }, warningTime);

    this.idleTimer = setTimeout(() => {
      this.logout(true);
    }, timeoutMs);
  }

  resetTimers(): void {
    this.startTimers();
  }

  private clearTimers(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    if (this.warningTimer !== null) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  private showWarningDialog(): void {
    this.isWarningVisible = true;
  
    const factory = this.resolver?.resolveComponentFactory(TimeoutWarningComponent);
    this.modalRef = factory.create(this.injector);
    this.appRef?.attachView(this.modalRef.hostView);
  
    const domElem = (this.modalRef.hostView as any).rootNodes[0] as HTMLElement;
    document.body?.appendChild(domElem);
  
    this.modalRef.instance.onLogout = () => {
      this.logout(true);
    };
  
    (this.modalRef.instance as TimeoutWarningComponent).onExtend = () => {
      this.isWarningVisible = false;
      this.destroyModal();
      this.resetTimers();
    };
  }
  

  private destroyModal(): void {
    if (this.modalRef) {
      this.appRef?.detachView(this.modalRef.hostView);
      this.modalRef?.destroy();
      this.modalRef = null;
    }
  }

  logout(forced: boolean): void {
    this.clearTimers();
    this.destroyModal();
    this.isWarningVisible = false;    
    // Perform logout logic here
    if (forced) {
      this.peAuthService.logout();
  //     localStorage.setItem('logout-event', Date.now().toString());

  //       setTimeout(() => {
  //   localStorage.clear();
  //   sessionStorage.clear();

  //   // 🔹 Auth0 logout (redirects properly)
  //   this._authService.logout({
  //     logoutParams: { returnTo: window.location.origin }
  //   }).subscribe({
  //     complete: () => this._router.navigate(['/login'])
  //   });
  // }, 200);
  }
}
}